import { Button, Paragraph } from '@cambridgeassessment/cambridge-ui';
import pluralize from 'pluralize';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import FailedSubmissionsTable from './FailedSubmissionsTable';
import { ButtonWrapper, StyledResultsBlock, StyledTitle } from './SubmissionFailures.styles';
import * as thunks from '../../../store/rmaSubmissionFailures/thunks';
import { SearchValues } from '../../../types/RmaErrors';

type Props = {
  inputValues: SearchValues;
};

const FailedSubmissionsList: React.FC<Props> = ({ inputValues }) => {
  const dispatch = useDispatch();
  const {
    userData: { businessUnit },
    series: { seriesData },
    rmaSubmissionFailures: { rmaErrors, total, isRmaErrorsLoading, nextPageUrl },
  } = useSelector((state: RootState) => state);

  const onResubmitClick = () => {
    dispatch(thunks.updateSectionsWithRmaErrors(businessUnit?.id, inputValues));
  };

  const searchSummaryText = React.useMemo(() => {
    const summaryParts: string[] = [];

    const seriesName = seriesData?.find((series) => series.id === Number(inputValues.seriesId))?.name;

    if (seriesName) {
      summaryParts.push(`Series - ${seriesName}`);
    }
    if (inputValues.assessmentCode) {
      summaryParts.push(`Syllabus number - ${inputValues.assessmentCode}`);
    }
    if (inputValues.componentNumber) {
      summaryParts.push(`Component number - ${inputValues.componentNumber}`);
    }
    if (inputValues.centreId) {
      summaryParts.push(`Centre number - ${inputValues.centreId}`);
    }
    if (inputValues.learnerNumber) {
      summaryParts.push(`Candidate number - ${inputValues.learnerNumber}`);
    }
    if (inputValues.errorType) {
      summaryParts.push(`Error type - ${inputValues.errorType}`);
    }

    return summaryParts.join(', ');
  }, [inputValues, seriesData]);

  return (
    <div>
      <StyledResultsBlock>
        <StyledTitle data-qa-id='search-result-title'>{`Results for: ${searchSummaryText}`}</StyledTitle>
        {rmaErrors && rmaErrors.length > 0 && (
          <ButtonWrapper>
            <Button data-qa-id='rma-resubmit-button' onClick={onResubmitClick}>
              Resubmit sections to RM
            </Button>
          </ButtonWrapper>
        )}
      </StyledResultsBlock>
      {total > 0 && <Paragraph>{`${pluralize('error', total, true)} returned`}</Paragraph>}
      <FailedSubmissionsTable
        businessUnitId={businessUnit?.id ?? ''}
        inputValues={inputValues}
        rmaErrors={rmaErrors}
        isRmaLoading={isRmaErrorsLoading}
        hasNextUrl={!!nextPageUrl}
      />
    </div>
  );
};

export default FailedSubmissionsList;
